import React, { useEffect, useMemo, useState } from 'react'
import { Loader, Message, Modal } from 'semantic-ui-react'
import { GetNotificationGroupCustomerList, GetTempleteList, GetUserListForGroup } from 'hooks'
import { AuthService, MaintenanceService, PaginationType } from 'utility'
import { MaintenanceTaskModal } from './MaintenanceTaskModal'
import { maintenanceApiError } from './maintenance-utils'

/**
 * "Create maintenance task" from an inspection finding or a ticket (EAUDITOR-408): loads the prefill
 * (GET Maintenance/FromFinding) and opens the normal create form with it - asset, finding, notes, photos, priority,
 * department, title and instructions filled in; the task keeps the source links (ids only).
 * Mount it only while open (it loads the template / user / department lists).
 * Props: auditId + questionId (+ answerId) or ticketId, onClose(created?).
 */
export const CreateMaintenanceFromFinding = ({ auditId, questionId, answerId, ticketId, onClose }) => {
    const companyId = AuthService.getUser()?.CompanyId
    const { options: templateOptions } = GetTempleteList(PaginationType.All)
    const { options: userOptions } = GetUserListForGroup(PaginationType.All)
    const { options: customerOptions } = GetNotificationGroupCustomerList(companyId)
    const [prefill, setPrefill] = useState(null)
    const [error, setError] = useState('')

    useEffect(() => {
        let alive = true
        MaintenanceService.fromFinding({ auditId, questionId, answerId, ticketId })
            .then(result => alive && setPrefill(result))
            .catch(err => alive && setError(maintenanceApiError(err, 'The finding could not be loaded.')))
        return () => {
            alive = false
        }
    }, [auditId, questionId, answerId, ticketId])

    const presetAsset = useMemo(
        () => (prefill?.AssetId ? { AssetId: prefill.AssetId, AssetName: prefill.AssetName } : null),
        [prefill],
    )

    if (error || !prefill) {
        return (
            <Modal open onClose={() => onClose()} size="tiny">
                <Modal.Content>
                    {error ? <Message negative content={error} /> : <Loader active inline="centered" content="Loading the finding" />}
                </Modal.Content>
            </Modal>
        )
    }

    return (
        <MaintenanceTaskModal
            open
            onClose={onClose}
            presetAsset={presetAsset}
            prefill={prefill}
            templateOptions={templateOptions}
            userOptions={userOptions}
            customerOptions={customerOptions}
        />
    )
}
